import type { CompletionSettings } from './completion-settings';
import { DeliveryStatus, InvoicedStatus } from './fulfilment-status';
import type {
  InvoiceCompletionFacts,
  InvoiceCompletionSettings
} from './invoice-completion-settings';

/** A completion condition the account requires that a document does not meet yet. */
export const CompletionCondition = {
  /** Order or requisition: its status is below the completion base status. */
  STATUS_NOT_REACHED: 'STATUS_NOT_REACHED',
  DELIVERY_INCOMPLETE: 'DELIVERY_INCOMPLETE',
  INVOICING_INCOMPLETE: 'INVOICING_INCOMPLETE',
  /** Invoice: not `APPROVED`. */
  NOT_APPROVED: 'NOT_APPROVED',
  NOT_PAID: 'NOT_PAID',
  NOT_POSTED: 'NOT_POSTED'
} as const;

export type CompletionCondition = (typeof CompletionCondition)[keyof typeof CompletionCondition];

/** What the completion settings check on one purchase order or requisition. */
export interface DocumentCompletionFacts {
  readonly hasReachedBaseStatus: boolean;
  readonly deliveryStatus: DeliveryStatus;
  readonly invoicedStatus: InvoicedStatus;
}

/** The conditions an order or requisition misses; only those the settings require. */
export function unmetDocumentCompletionConditions(
  settings: Pick<CompletionSettings<string>, 'isDeliveryRequired' | 'isInvoicingRequired'>,
  facts: DocumentCompletionFacts
): CompletionCondition[] {
  const unmet: CompletionCondition[] = [];
  if (!facts.hasReachedBaseStatus) unmet.push(CompletionCondition.STATUS_NOT_REACHED);
  if (settings.isDeliveryRequired && facts.deliveryStatus !== DeliveryStatus.DELIVERED) {
    unmet.push(CompletionCondition.DELIVERY_INCOMPLETE);
  }
  if (settings.isInvoicingRequired && facts.invoicedStatus !== InvoicedStatus.INVOICED) {
    unmet.push(CompletionCondition.INVOICING_INCOMPLETE);
  }
  return unmet;
}

/** The conditions an invoice misses; only those the settings require. */
export function unmetInvoiceCompletionConditions(
  settings: InvoiceCompletionSettings,
  facts: InvoiceCompletionFacts
): CompletionCondition[] {
  const unmet: CompletionCondition[] = [];
  if (settings.isApprovalRequired && !facts.isApproved)
    unmet.push(CompletionCondition.NOT_APPROVED);
  if (settings.isPaymentRequired && !facts.isPaid) unmet.push(CompletionCondition.NOT_PAID);
  if (settings.isPostingRequired && !facts.isPosted) unmet.push(CompletionCondition.NOT_POSTED);
  return unmet;
}
